import FieldType from "@/utils/constants/FieldType.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import {ValueFormatterParams} from "ag-grid-community";
import Translator from "@/utils/common/Translator.ts";
import ColumnFiltrationFactory, {FilterData} from "@/utils/UIConstants/ColumnFiltrationFactory.ts";
import AGGridDateTimeFilter from "@/components/inputs/AGGridDateTimeFilter.vue";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {KeyCreatorParams} from "ag-grid-enterprise";
import DTOTabularResult = namaTypes.DTOTabularResult;
import PagedDTOTabularResult = namaTypes.PagedDTOTabularResult;
import EntityReferenceData = namaTypes.EntityReferenceData;

export default class ColumnFiltrationServerSideFactory
{
  static fetchFilterData(column: namaTypes.ListScreenColumn, entityType: string, layout?: namaTypes.ListScreenLayout & namaTypes.SearcherScreenLayout): FilterData | null | undefined {
    let type = column.metaData?.fieldType;
    if (column.arabicTemplate || column.englishTemplate || column.sources)
      return;
    if (!type)
      return;
    switch (type) {
      case FieldType.Text:
      case FieldType.Link:
      case FieldType.Email:
      case FieldType.BigText:
      case FieldType.PhoneNumber:
      case FieldType.FieldID:
      case FieldType.Measures:
      case FieldType.Color:
      case FieldType.LongToken:
      case FieldType.LatLng:
        return new FilterData("agTextColumnFilter", {
          textFormatter: (value: string): string | null => value == null ? null : StringUtils.replaceArNumerals(value),
          textMatcher: ColumnFiltrationFactory.textMatcher,
          maxNumConditions: 1,
          buttons: ["reset"]
        });
      case FieldType.Decimal:
      case FieldType.Long:
      case FieldType.Integer:
        return new FilterData("agNumberColumnFilter", {
          inRangeInclusive: true,
          maxNumConditions: 1,
          buttons: ["reset"]
        });
      case FieldType.Boolean:
        return new FilterData("agSetColumnFilter",
          {
            buttons: ["reset"],
            applyMiniFilterWhileTyping: true,
            values: ['true', 'false'],
            valueFormatter: (params: ValueFormatterParams) => Translator.translateId(params.value),
          });
      case FieldType.Date:
        return new FilterData("agDateColumnFilter", {
            minValidYear: 1800,
            maxValidYear: 2999,
            buttons: ["reset"],
            maxNumConditions: 1,
          }
        );
      case FieldType.DateAndTime://TODO:: Need to handle equality with seconds
        // TODO :: case FieldType.Time: :: Need to handle equality with seconds and all operators not working
        return new FilterData(AGGridDateTimeFilter, {
          fieldType: type
        });
      case FieldType.FeatureID:
      case FieldType.EntityType:
      case FieldType.Enum:
        return new FilterData("agSetColumnFilter", {
          buttons: ["reset"],
          applyMiniFilterWhileTyping: true,
          valueFormatter: (params: ValueFormatterParams) =>
            !params.value ? Translator.translateId('blank') : Translator.translate(params.value, column.metaData?.enumTypeName),
          values: async (params) => {
            let colId = params.colDef.colId;
            let values = await NamaCommon.namaFetch(NamaURLS.FETCHDistinctValuesForAGGridColumnFiltering, {
              params: {
                column: colId,
                entityType: entityType,
                fieldType: column.metaData?.fieldType,
                referenceTo: column.metaData?.referencedEntityType,
                queriedClass: layout?.queriedClass
              },
              noLoadingIndicator: true
            });
            let result: string[] = [];
            if (values && NamaCommon.isSuccess(values) && (values as PagedDTOTabularResult).list) {
              let list = (values as PagedDTOTabularResult).list as DTOTabularResult[];
              result.push(...list.map(l => (l.values ?? {})[colId]));
            }
            params.success(result);
          }
        });
      case FieldType.Reference:
      case FieldType.Genericreference:
        return new FilterData("agMultiColumnFilter", {
          filters: [
            {
              filter: ColumnFiltrationFactory.fetchAGReferenceFilter(),
              filterParams: {
                metaData: column.metaData,
              }
            },
            {
              filter: "agSetColumnFilter",
              filterParams: {
                applyMiniFilterWhileTyping: true,
                valueFormatter: (params: ValueFormatterParams) =>
                  !params.value ? Translator.translateId('blank') : Translator.translateRef(params.value),
                values: async (params) => {
                  let colId = params.colDef.colId;
                  let values = await NamaCommon.namaFetch(NamaURLS.FETCHDistinctValuesForAGGridColumnFiltering, {
                    params: {
                      column: colId,
                      entityType: entityType,
                      fieldType: column.metaData?.fieldType,
                      referenceTo: column.metaData?.referencedEntityType,
                      queriedClass: layout?.queriedClass
                    },
                    noLoadingIndicator: true
                  });
                  let result: EntityReferenceData[] = [];
                  if (values && NamaCommon.isSuccess(values) && (values as PagedDTOTabularResult).list) {
                    let list = (values as PagedDTOTabularResult).list as DTOTabularResult[];
                    result.push(...list.map(l => (l.values ?? {})[colId]));
                  }
                  params.success(result);
                },
                keyCreator: (params: KeyCreatorParams) => params.value ? params.value.id : null
              },
            }
          ]
        });
      case FieldType.Binary:
      default:
        return;
    }
  }
}
